import EditLayout from 'layouts/edit-layout/EditLayout.tsx'
import { useTranslation } from 'react-i18next'
import styles from './CancelSupplierSwitch.module.scss'
import { useStoreSelector } from 'hooks/store.ts'
import { Banner, Button, Card, Form, Heading, Select } from '@boltenergy-be/design-system'
import { FormProvider, useForm } from 'react-hook-form'
import { CancelSupplierSwitchInputs } from './types.ts'
import ControlledMultiSelect from 'components/form-fields/multi-select/ControlledMultiSelect.tsx'
import { formatAddress } from 'utils/user.ts'
import { AddressFormats } from 'types/format.ts'
import { BillingContractWithServiceContracts, CONTRACT_STATUSES, RECONFIRM_ENERGY_SUPPLIERS } from '@boltenergy-be/shared'
import { selectCurrentAccount } from 'store/contact/slice.ts'
import boltBackend from 'lib/bolt-backend'
import { log } from 'utils/logging.ts'
import { Flow } from 'types/logging.ts'
import { useMemo, useState } from 'react'
import { routes } from 'types/routes.ts'
import parse from 'html-react-parser'
import { checkContractStatus } from 'utils/contracts.ts'
import { trackEvent } from 'utils/tracking.ts'

const CancelSupplierSwitch = () => {
  // Redux store
  const { contact, billingContracts } = useStoreSelector((store) => store.contact)
  const account = useStoreSelector(selectCurrentAccount)

  // i18n
  const { t } = useTranslation(['cancelSupplierSwitch', 'common', 'validation', 'errors'])

  // React hook form
  const hookForm = useForm<CancelSupplierSwitchInputs>()
  const { handleSubmit, register, formState } = hookForm

  // Local state
  const [success, setSuccess] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<boolean>(false)

  // Constants
  const terminatedContracts = useMemo(() => {
    const contracts = new Set<BillingContractWithServiceContracts>()
    Object.values(account.billingContracts).forEach((contractNumber) => {
      const contract = billingContracts[contractNumber]
      if (checkContractStatus(contract.serviceContracts.electricity, CONTRACT_STATUSES.terminated)) {
        contracts.add(contract)
      }
    })
    return contracts
  }, [account.billingContracts, billingContracts])

  /**
   * Submits the cancel supplier switch form
   * @param {CancelSupplierSwitchInputs} data
   */
  const submit = async (data: CancelSupplierSwitchInputs) => {
    setLoading(true)
    setError(false)

    try {
      if (!data.addresses.length) {
        throw new Error('No contracts to reconfirm.')
      }

      const responses = await Promise.all(
        data.addresses.map((contractNumber) =>
          boltBackend.reconfirmBillingContract(contractNumber, {
            previousSupplier: data.supplier === 'unknown' ? undefined : data.supplier
          })
        )
      )

      if (responses.some((res) => !res.success)) {
        setError(true)
        log({
          error: 'One or more contracts could not be reconfirmed.',
          identifier: `[${Flow.SERVICE_CONTRACT}:reconfirm]`,
          metaData: {
            reconfirm_body: data,
            reconfirm_responses: {
              responses: JSON.stringify(responses, null, 2)
            }
          }
        })
        return
      }

      trackEvent({ category: 'cancel_supplier_switch', action: 'finish' }, { supplier: data.supplier })

      setSuccess(true)
    } catch (error) {
      setError(true)
      trackEvent({ category: 'cancel_supplier_switch', action: 'fail' }, { supplier: data.supplier })
      log({
        error: error instanceof Error ? error : 'unknown error when reconfirming service contracts',
        identifier: `[${Flow.SERVICE_CONTRACT}:reconfirm]`,
        metaData: {
          reconfirm_body: data
        }
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <EditLayout
      withFooter
      title={
        <Heading as="h1" variant="h5">
          {t(($) => $.title)}
        </Heading>
      }
      backUrl={routes.OVERVIEW}
    >
      {success ? (
        <Card className={styles['success-card']}>
          <Heading as="h2" variant="h4">
            {t(($) => $.success.title)}
          </Heading>
          <p>{t(($) => $.success.description)}</p>
        </Card>
      ) : (
        <Card className={styles.card}>
          <Heading as="h2" variant="h4">
            {t(($) => $.card.title, {
              name: contact?.firstName || ''
            })}
          </Heading>
          <p>{parse(t(($) => $.card.description))}</p>

          <FormProvider {...hookForm}>
            <Form onSubmit={handleSubmit(submit)}>
              <ControlledMultiSelect
                grid="full"
                identifier="addresses"
                label={t(($) => $.card.form.address.label)}
                placeholder={t(($) => $.card.form.address.placeholder)}
                noResultsText={t(($) => $.card.form.address.noResults)}
                options={Array.from(terminatedContracts)?.map((contract) => ({
                  label: formatAddress(contract.serviceContracts.electricity.deliveryPoint.address, AddressFormats.FULL_SINGLE_LINE)!,
                  value: contract.contractNumber
                }))}
              />

              <Select
                grid="full"
                label={t(($) => $.card.form.supplier.label)}
                error={formState.errors?.supplier?.message}
                {...register('supplier', {
                  required: t(($) => $.required, {
                    ns: 'validation'
                  })
                })}
              >
                <option key="placeholder" value="">
                  {t(($) => $.card.form.supplier.placeholder)}
                </option>
                {Object.values(RECONFIRM_ENERGY_SUPPLIERS).map((supplier) => (
                  <option key={supplier} value={supplier}>
                    {supplier}
                  </option>
                ))}
                <option key="unknown" value="unknown">
                  {t(($) => $.card.form.supplier.unknown)}
                </option>
              </Select>

              <div className="grid-col-full flex-col gap-300 mt-200">
                <Button {...{ loading }} disabled={success || error}>
                  {t(($) => $.send, {
                    ns: 'common'
                  })}
                </Button>
                <small className="text-subdued">{t(($) => $.card.form.note)}</small>
                {error && (
                  <Banner
                    type="blocking"
                    title={t(($) => $.default, {
                      ns: 'errors'
                    })}
                  />
                )}
              </div>
            </Form>
          </FormProvider>
        </Card>
      )}
    </EditLayout>
  )
}

export default CancelSupplierSwitch
